/**
 * Shared component that renders the Zegen DMS documents attached to
 * an invoice: signed invoice PDF, delivery notes (multiple), and
 * faktur pajak (multiple).
 *
 * Used by:
 *   - Customer's InvoiceDetailPage (renders inside its own Card)
 *   - Backoffice standalone dialog (rendered inside <Dialog>)
 *   - Backoffice OrderDetailDialog (rendered as a Section)
 *
 * The panel renders the CONTENT ONLY (no outer Card/Dialog wrapper).
 * The caller places it inside whatever container fits its layout.
 *
 * Data-fetching + download plumbing is owned by the caller and passed
 * in as props. That keeps the panel purely presentational so we can
 * reuse the same JSX with different hooks (useInvoices for customer,
 * useBackofficeInvoice for backoffice, or ad-hoc fetch for embedded
 * uses).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Skeleton,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Description as DescriptionIcon,
  LocalShipping as ShippingIcon,
  ReceiptLong as FakturIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DmsDocumentResponse } from '../services/invoiceService';

export interface DmsDocumentsPanelProps {
  /** Metadata response from GET /invoices/{id}/dms-documents. */
  dmsDocuments: DmsDocumentResponse | null;
  /** True while the initial fetch is in flight — renders skeleton. */
  isLoading: boolean;
  /** Force-refetch from Zegen. Backoffice hook forwards `?refresh=1`. */
  onRefresh: () => void;
  /** Download a specific DMS document by type + optional index. */
  onDownload: (docType: 'signed-invoice' | 'delivery-note' | 'faktur-pajak', docIndex?: number) => void;
  /**
   * Whether to render the section-header Refresh icon. Backoffice
   * ops want it visible (they know when a doc has just landed on
   * Zegen); customer flow has it too but hides on read-only
   * embeds if the caller sets false.
   */
  showRefresh?: boolean;
}

const DmsDocumentsPanel: React.FC<DmsDocumentsPanelProps> = ({
  dmsDocuments,
  isLoading,
  onRefresh,
  onDownload,
  showRefresh = true,
}) => {
  // Every visible string flows through i18next -- the app-level
  // language switcher (used by every other page) chooses between
  // Bahasa Indonesia and English. Prior version rendered both
  // languages inline (e.g. "Invoice Bertandatangan Digital / Signed
  // Invoice"), which circumvented the switcher. Keys live under
  // the `dms` namespace in frontend/src/i18n/locales/{en,id}.json.
  const { t } = useTranslation();

  // Loading state — skeleton keeps the layout stable.
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="rectangular" height={100} sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  // No data / DMS disabled — render nothing so the container decides
  // whether to hide itself or show a "not available" fallback.
  if (!dmsDocuments || !dmsDocuments.dms_enabled || !dmsDocuments.status_documents) {
    return null;
  }

  const {
    status_documents,
    missing_documents,
    fetch_error,
    signed_invoice,
    delivery_notes,
    faktur_pajak,
  } = dmsDocuments;

  const hasAnyDoc =
    Boolean(signed_invoice) ||
    (delivery_notes && delivery_notes.length > 0) ||
    (faktur_pajak && faktur_pajak.length > 0);

  return (
    <Box>
      {/* Header — title, status chip, refresh */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('dms.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={
              status_documents === 'Complete'
                ? t('dms.status.complete')
                : t('dms.status.incomplete')
            }
            color={status_documents === 'Complete' ? 'success' : 'warning'}
            size="small"
          />
          {showRefresh && (
            <Tooltip title={t('dms.refreshTooltip')}>
              <IconButton
                size="small"
                onClick={onRefresh}
                aria-label={t('dms.refreshAriaLabel')}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Missing-documents warning (Zegen reports which types haven't landed yet) */}
      {missing_documents && missing_documents.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('dms.missingDocuments', { list: missing_documents.join(', ') })}
        </Alert>
      )}

      {/* Fetch-error notice — cache being served after a Zegen fetch fail */}
      {fetch_error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('dms.fetchErrorNotice')}
        </Alert>
      )}

      {/* If Zegen returned no docs at all */}
      {!hasAnyDoc && !fetch_error && (
        <Alert severity="info">{t('dms.noDocuments')}</Alert>
      )}

      <List disablePadding>
        {/* Signed Invoice — always at most one */}
        {signed_invoice && (
          <ListItem divider sx={{ px: 0 }}>
            <ListItemIcon>
              <DescriptionIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={t('dms.signedInvoice.title')}
              secondary={
                signed_invoice.sign_status === 'signed_with_ematerai'
                  ? t('dms.signedInvoice.signedWithEmaterai')
                  : t('dms.signedInvoice.signedOnly')
              }
            />
            <ListItemSecondaryAction>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => onDownload('signed-invoice')}
              >
                {t('dms.download')}
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
        )}

        {/* Delivery Notes — zero or more, one row each */}
        {delivery_notes && delivery_notes.map((dn, index) => (
          <ListItem key={`dn-${index}`} divider sx={{ px: 0 }}>
            <ListItemIcon>
              <ShippingIcon color="info" />
            </ListItemIcon>
            <ListItemText
              primary={t('dms.deliveryNote')}
              secondary={dn.dn_code}
            />
            <ListItemSecondaryAction>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => onDownload('delivery-note', index)}
              >
                {t('dms.download')}
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
        ))}

        {/* Faktur Pajak — zero or more, one row each */}
        {faktur_pajak && faktur_pajak.map((fp, index) => (
          <ListItem key={`fp-${index}`} divider sx={{ px: 0 }}>
            <ListItemIcon>
              <FakturIcon color="secondary" />
            </ListItemIcon>
            <ListItemText
              primary={t('dms.fakturPajak')}
              secondary={t('dms.fakturPajakNumber', { number: fp.nomor_faktur })}
            />
            <ListItemSecondaryAction>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => onDownload('faktur-pajak', index)}
              >
                {t('dms.download')}
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default DmsDocumentsPanel;
